/**
 * RendPleX Scenario Analyzer 2.0 — P3A Offer Price Intelligence tab.
 *
 * User-facing question:
 *   "What purchase price would make this deal meet my target?"
 *
 * Calls POST /api/scenario/offer-price. Deterministic. Never fabricates
 * a successful solution: on impossible targets it renders the
 * `closest` block returned by the backend. No DB. No LLM.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import {
  Target, Loader2, CheckCircle2, AlertTriangle,
  ArrowDown, DollarSign,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const METRICS = [
  { key: 'monthly_cash_flow', i18n: 'offerPrice.metricMonthlyCashFlow', suffix: 'offerPrice.monthlyCashFlowSuffix', default: 500,   decimals: 0, prefix: '$' },
  { key: 'cap_rate',          i18n: 'offerPrice.metricCapRate',         suffix: 'offerPrice.capRateSuffix',         default: 5.5,  decimals: 2, prefix: '' },
  { key: 'dscr',              i18n: 'offerPrice.metricDscr',            suffix: 'offerPrice.dscrSuffix',            default: 1.20, decimals: 2, prefix: '' },
  { key: 'cash_on_cash',      i18n: 'offerPrice.metricCashOnCash',      suffix: 'offerPrice.cashOnCashSuffix',      default: 5.0,  decimals: 2, prefix: '' },
  { key: 'roi',               i18n: 'offerPrice.metricRoi',             suffix: 'offerPrice.roiSuffix',             default: 5.0,  decimals: 2, prefix: '' },
];

const fmtMoney = (n) => (n === null || n === undefined || Number.isNaN(n))
  ? '—'
  : `$${Math.round(n).toLocaleString()}`;
const fmtPct = (n, dec = 2) => (n === null || n === undefined || Number.isNaN(n))
  ? '—'
  : `${n.toFixed(dec)}%`;
const fmtRatio = (n, dec = 2) => (n === null || n === undefined || Number.isNaN(n))
  ? '—'
  : n.toFixed(dec);

const formatValue = (metricKey, value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (metricKey === 'monthly_cash_flow') return fmtMoney(value);
  if (metricKey === 'dscr') return fmtRatio(value);
  return fmtPct(value); // cap_rate, cash_on_cash, roi
};

const OfferPriceTab = ({ scenario, onApply }) => {
  const { t } = useTranslation();
  const [metricKey, setMetricKey] = useState('monthly_cash_flow');
  const [targetValue, setTargetValue] = useState(500);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const metricDef = useMemo(
    () => METRICS.find((m) => m.key === metricKey) || METRICS[0],
    [metricKey]
  );

  const onMetricChange = (e) => {
    const nextKey = e.target.value;
    setMetricKey(nextKey);
    // Reset target to the metric's sensible default so users don't try
    // to hit "5.5 DSCR" by accident when switching from cap_rate.
    const def = METRICS.find((m) => m.key === nextKey);
    if (def) setTargetValue(def.default);
    setResult(null);
    setError(null);
  };

  const run = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/scenario/offer-price`,
        {
          scenario,
          target_metric: metricKey,
          target_value: Number(targetValue),
        },
        { withCredentials: true }
      );
      setResult(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || t('offerPrice.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    if (!result || result.status !== 'solved' || result.offer_price === null) return;
    onApply({ purchase_price: result.offer_price });
    toast.success(t('offerPrice.applied'));
    setResult(null);
  };

  const status = result?.status;
  const isSolved = status === 'solved';
  const isAlreadyMet = status === 'already_met';
  const isImpossible = status === 'impossible';

  return (
    <div
      data-testid="offer-price-tab"
      role="region"
      aria-live="polite"
      aria-atomic="false"
      className="space-y-4"
    >
      {/* Controls row */}
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-end">
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold mb-1 block">
            {t('offerPrice.targetMetric')}
          </label>
          <select
            data-testid="offer-price-target-metric"
            value={metricKey}
            onChange={onMetricChange}
            className="w-full h-10 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#0F1419] text-slate-900 dark:text-slate-100 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {METRICS.map((m) => (
              <option key={m.key} value={m.key}>{t(m.i18n)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold mb-1 block">
            {t('offerPrice.targetValue')}
          </label>
          <div className="relative">
            {metricDef.prefix && (
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
            )}
            <input
              data-testid="offer-price-target-value"
              type="number"
              step={metricDef.decimals === 0 ? 1 : 0.01}
              value={targetValue}
              onChange={(e) => {
                const raw = e.target.value;
                const n = raw === '' ? 0 : Number(raw);
                if (Number.isFinite(n)) setTargetValue(n);
              }}
              className={`w-full h-10 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#0F1419] text-slate-900 dark:text-slate-100 ${
                metricDef.prefix ? 'pl-7' : 'pl-3'
              } pr-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500`}
            />
          </div>
        </div>
        <Button
          data-testid="offer-price-run-btn"
          onClick={run}
          disabled={loading}
          className="h-10 bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('offerPrice.computing')}</>)
                   : (<><Target className="w-4 h-4 mr-2" />{t('offerPrice.calculate')}</>)}
        </Button>
      </div>

      {error && (
        <p data-testid="offer-price-error" className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* Solved — show offer + resulting metrics + apply CTA */}
      {isSolved && (
        <div data-testid="offer-price-status-solved" className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F1419] p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('offerPrice.askingPrice')}
              </p>
              <p data-testid="offer-price-asking" className="font-primary font-bold text-lg text-slate-900 dark:text-slate-100 tabular-nums">
                {fmtMoney(result.asking_price)}
              </p>
            </div>
            <div className="rounded-lg border-2 border-indigo-400 dark:border-indigo-600 bg-indigo-50 dark:bg-indigo-950/30 p-3">
              <p className="text-[10px] uppercase tracking-wider text-indigo-700 dark:text-indigo-300 font-semibold">
                {t('offerPrice.targetOfferPrice')}
              </p>
              <p data-testid="offer-price-offer" className="font-primary font-bold text-lg text-indigo-700 dark:text-indigo-300 tabular-nums">
                {fmtMoney(result.offer_price)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F1419] p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('offerPrice.requiredReduction')}
              </p>
              <div className="flex items-baseline gap-1">
                <ArrowDown className="w-3 h-3 text-red-500 dark:text-red-400" />
                <p data-testid="offer-price-reduction-abs" className="font-primary font-bold text-lg text-red-600 dark:text-red-400 tabular-nums">
                  {fmtMoney(result.required_reduction_abs)}
                </p>
              </div>
              <p data-testid="offer-price-reduction-pct" className="text-[11px] text-slate-500 dark:text-slate-400 tabular-nums">
                −{result.required_reduction_pct?.toFixed(2)}%
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F1419] p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('offerPrice.targetResult')}
              </p>
              <p data-testid="offer-price-achieved" className="font-primary font-bold text-lg text-emerald-600 dark:text-emerald-400 tabular-nums">
                {formatValue(metricKey, result.achieved_value)}
                <span className="text-[10px] font-normal opacity-70">{t(metricDef.suffix)}</span>
              </p>
            </div>
          </div>

          {/* Resulting metrics grid */}
          <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F1419] p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold mb-3">
              {t('offerPrice.resultingMetrics')}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">MCF</p>
                <p data-testid="offer-price-result-mcf" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                  {fmtMoney(result.resulting_metrics?.monthly_cash_flow)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">NOI</p>
                <p data-testid="offer-price-result-noi" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                  {fmtMoney(result.resulting_metrics?.noi)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">Cap</p>
                <p data-testid="offer-price-result-cap" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                  {fmtPct(result.resulting_metrics?.cap_rate)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">DSCR</p>
                <p data-testid="offer-price-result-dscr" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                  {fmtRatio(result.resulting_metrics?.dscr)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">CoC</p>
                <p data-testid="offer-price-result-coc" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                  {fmtPct(result.resulting_metrics?.cash_on_cash)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('offerPrice.monthlyMortgage')}</p>
                <p data-testid="offer-price-result-mortgage" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                  {fmtMoney(result.resulting_metrics?.monthly_mortgage)}
                </p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center gap-2 text-sm">
              <DollarSign className="w-4 h-4 text-emerald-500" />
              <span className="text-slate-500 dark:text-slate-400">{t('offerPrice.cashRequired')}:</span>
              <span data-testid="offer-price-cash-required" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                {fmtMoney(result.cash_required)}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              data-testid="offer-price-apply-btn"
              onClick={apply}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {t('offerPrice.applyOffer')}
            </Button>
          </div>
        </div>
      )}

      {/* Already met */}
      {isAlreadyMet && (
        <div data-testid="offer-price-status-already-met"
             className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20 p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-emerald-800 dark:text-emerald-300">{t('offerPrice.alreadyMet')}</p>
            <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-1 tabular-nums">
              {t('offerPrice.askingPrice')}: {fmtMoney(result.asking_price)} · {formatValue(metricKey, result.achieved_value)}
              {t(metricDef.suffix)}
            </p>
          </div>
        </div>
      )}

      {/* Impossible + closest */}
      {isImpossible && (
        <div data-testid="offer-price-status-impossible"
             className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 p-4">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800 dark:text-amber-300">{t('offerPrice.notAchievable')}</p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">{t('offerPrice.closestExplain')}</p>
            </div>
          </div>
          {result.closest && (
            <div data-testid="offer-price-closest" className="rounded-md border border-amber-200 dark:border-amber-900 bg-white dark:bg-[#0F1419] p-3">
              <p className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400 font-semibold mb-2">
                {t('offerPrice.closestResult')}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {t('offerPrice.targetOfferPrice')}
                  </p>
                  <p data-testid="offer-price-closest-price" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                    {fmtMoney(result.closest.offer_price)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">MCF</p>
                  <p data-testid="offer-price-closest-mcf" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                    {fmtMoney(result.closest.resulting_metrics?.monthly_cash_flow)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">DSCR</p>
                  <p data-testid="offer-price-closest-dscr" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                    {fmtRatio(result.closest.resulting_metrics?.dscr)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {t('offerPrice.cashRequired')}
                  </p>
                  <p data-testid="offer-price-closest-cash" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                    {fmtMoney(result.closest.cash_required)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <p className="text-[11px] text-slate-500 dark:text-slate-400 italic">
        {t('decision.notesDeterministic')}
      </p>
    </div>
  );
};

export default OfferPriceTab;
